import { useState, useEffect } from 'react';
import { announcementService, assemblyService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import SearchableSelect from 'components/SearchableSelect';
import PropertyCombobox from 'components/PropertyCombobox';
import styles from './ComunicadoFormModal.module.css';

// Límites reales — mismos que valida el backend (announcementsController.js).
// Un comunicado es un aviso corto, no un documento; el tope de body es
// generoso a propósito (uno inusualmente largo, no uso normal) — pasarse de
// esto ahora se RECHAZA con mensaje en vez de recortarse en silencio como
// antes (confirmado con Marlon 2026-09-20).
const LIMITE_TITULO = 200;
const LIMITE_BODY   = 20000;

// Editor (crear / editar). onDone(refresca).
// asmAdminAssemblies != null → el usuario es assembly_admin: solo puede publicar
// comunicados atados a una de SUS asambleas, y el conjunto se deriva de ella.
export default function ComunicadoFormModal({ initial, properties, fixedPropertyId, asmAdminAssemblies, onClose, onDone }) {
  const editing = !!initial?.id;
  const esAsmAdmin = Array.isArray(asmAdminAssemblies);
  const [propertyId, setPropertyId] = useState(initial?.property_id || fixedPropertyId || (properties[0]?.id ?? ''));
  const [title, setTitle] = useState(initial?.title || '');
  const [body,  setBody]  = useState(initial?.body || '');
  const [assemblyId, setAssemblyId] = useState(initial?.assembly_id || (esAsmAdmin && asmAdminAssemblies.length === 1 ? asmAdminAssemblies[0].id : ''));
  const [assemblies, setAssemblies] = useState([]);
  const [email, setEmail] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (esAsmAdmin) { setAssemblies(asmAdminAssemblies); return; }
    if (!propertyId) { setAssemblies([]); return; }
    assemblyService.getAll({ property_id: propertyId, limit: 100 })
      .then(r => setAssemblies(r.data.data || []))
      .catch(() => setAssemblies([]));
  }, [propertyId, esAsmAdmin, asmAdminAssemblies]);

  // assembly_admin: el conjunto lo dicta la asamblea elegida
  useEffect(() => {
    if (!esAsmAdmin || !assemblyId) return;
    const a = asmAdminAssemblies.find(x => String(x.id) === String(assemblyId));
    if (a?.property_id) setPropertyId(a.property_id);
  }, [esAsmAdmin, assemblyId, asmAdminAssemblies]);

  const guardar = async (publicar) => {
    const t = title.trim(), b = body.trim();
    // Antes estas 3 condiciones solo se usaban para poner `disabled` en los
    // botones — un clic sin diligenciar nada no hacía nada y no mostraba
    // ningún mensaje. Validar acá (igual que el resto de la app) para que
    // siempre se explique qué falta.
    if (!t) return setError('El título es obligatorio.');
    if (!b) return setError('El contenido es obligatorio.');
    if (esAsmAdmin && !assemblyId) return setError('Debes seleccionar tu asamblea.');
    if (t.length > LIMITE_TITULO) return setError(`El título no puede tener más de ${LIMITE_TITULO} caracteres`);
    if (b.length > LIMITE_BODY)   return setError(`El contenido no puede tener más de ${LIMITE_BODY.toLocaleString('es-CO')} caracteres`);
    setBusy(true); setError('');
    try {
      const payload = { property_id: propertyId, title: t, body: b,
        assembly_id: assemblyId || null };
      if (editing) {
        await announcementService.update(initial.id, payload);
        if (publicar) await announcementService.publish(initial.id, email);
      } else {
        await announcementService.create({ ...payload, publish: publicar, email: publicar && email });
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo guardar.');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalComunicado}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><i className={`fas fa-bullhorn ${styles.iconoTitulo}`} />
            {editing ? 'Editar comunicado' : 'Nuevo comunicado'}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <AvisoModal onClick={() => setError('')}>{error}</AvisoModal>}

          {!fixedPropertyId && properties.length > 1 && !editing && (
            <div className="form-group">
              <label className="form-label">Conjunto *</label>
              <PropertyCombobox value={propertyId} allowEmpty={false} minWidth={0} maxWidth={9999}
                onChange={(id) => setPropertyId(id)} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Título *</label>
            <input className="form-control" value={title} maxLength={200}
              onChange={e => setTitle(e.target.value)} placeholder="Ej: Corte de agua programado" />
          </div>

          <div className="form-group">
            <label className="form-label">Contenido *</label>
            <textarea className="form-control" rows="6" value={body} maxLength={LIMITE_BODY}
              onChange={e => setBody(e.target.value)}
              placeholder="Escribe el comunicado. Los saltos de línea se respetan." />
            <div className={styles.contador}>
              {body.length.toLocaleString('es-CO')} / {LIMITE_BODY.toLocaleString('es-CO')}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              {esAsmAdmin ? 'Asamblea *' : 'Asamblea relacionada (opcional)'}
            </label>
            <SearchableSelect value={assemblyId} onChange={(id) => setAssemblyId(id)} disabled={editing} minWidth={0} maxWidth={9999} ariaLabel="Asamblea"
              placeholder={esAsmAdmin ? '— Selecciona tu asamblea —' : '— Comunicado general del conjunto —'}
              options={assemblies.map(a => ({ value: a.id, label: `#${a.number_assembly} · ${new Date(a.date).toLocaleDateString('es-CO')}` }))} />
            {esAsmAdmin && (
              <div className={styles.nota}>
                Como administrador de asamblea, tus comunicados van siempre atados a tu asamblea.
              </div>
            )}
          </div>

          <label className={styles.casilla}>
            <input type="checkbox" checked={email} onChange={e => setEmail(e.target.checked)}
              disabled={editing && !!initial.emailed_at} />
            Enviar también por correo a todos los propietarios al publicar
            {editing && initial.emailed_at && <span className={styles.yaEnviado}> (ya se envió)</span>}
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-secondary" disabled={busy}
            onClick={() => guardar(false)}>
            <i className="fas fa-save" /> Guardar borrador
          </button>
          <button className="btn btn-primary" disabled={busy}
            onClick={() => guardar(true)}>
            <i className="fas fa-paper-plane" /> Publicar
          </button>
        </div>
      </div>
    </div>
  );
}
